import { useEffect, useMemo, useState } from 'react';
import { Header } from '@/components';
import { useAppStore } from '@/stores/appStore';
import { API_CONFIG } from '@/services/api';

type PodcastTarget = {
  showId: string;
  title: string;
  host?: string;
  publisher?: string;
  email?: string;
  contactEmail?: string;
  genres?: string;
  website?: string;
  description?: string;
  active?: boolean;
  guests?: boolean;
  downloadsPerEpisode?: number;
  weeklyDownloads?: number;
  socialReach?: number;
  frequency?: string;
  totalEpisodes?: number;
  foundedDate?: string;
  rss?: string;
  image?: string;
};

const LIST_ENTITY_TYPE = 'podcast';
const DEFAULT_LIST_STORAGE_KEY = 'profitibull_default_podcast_list';

const asText = (value: unknown): string => {
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  return '';
};

const asNumber = (value: unknown): number | undefined => {
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
};

const asBool = (value: unknown): boolean | undefined => {
  if (typeof value === 'boolean') return value;
  if (value === 'true') return true;
  if (value === 'false') return false;
  return undefined;
};

const formatNumber = (value: unknown) => {
  const n = asNumber(value);
  return typeof n === 'number' ? n.toLocaleString() : '—';
};

const formatDate = (value: unknown) => {
  const raw = asText(value);
  if (!raw) return '—';
  const parsed = new Date(raw);
  if (!Number.isFinite(parsed.getTime())) return raw;
  return parsed.toLocaleDateString();
};

const getByPath = (obj: Record<string, any>, path: string): unknown =>
  path.split('.').reduce<unknown>((acc, part) => {
    if (acc && typeof acc === 'object' && part in (acc as Record<string, unknown>)) {
      return (acc as Record<string, unknown>)[part];
    }
    return undefined;
  }, obj);

const normalizeShowId = (show: Record<string, any>) => {
  const candidates = [show.id, show.uuid, show.podcast_id, show.podcastId, show.slug];
  for (const v of candidates) {
    if (v != null && String(v).trim()) return String(v);
  }
  return asText(show.title) || asText(show.name) || 'unknown';
};

const imageUrl = (show: Record<string, any>) =>
  asText(show.image) ||
  asText(show.imageUrl) ||
  asText(show.image_url) ||
  asText(show.artwork_thumbnail_url) ||
  asText(show.artworkUrl) ||
  asText(show.artwork_url) ||
  asText(show.podcast?.image) ||
  asText(show.podcast?.imageUrl) ||
  asText(show.podcast?.artwork_url) ||
  asText(show.podcast?.artworkUrl);

const pickText = (show: Record<string, any>, keys: string[]) => {
  for (const key of keys) {
    const value = asText(key.includes('.') ? getByPath(show, key) : show[key]);
    if (value) return value;
  }
  return '';
};

const pickNumber = (show: Record<string, any>, keys: string[]) => {
  for (const key of keys) {
    const value = asNumber(key.includes('.') ? getByPath(show, key) : show[key]);
    if (typeof value === 'number') return value;
  }
  return undefined;
};

const pickHost = (show: Record<string, any>): string => {
  const direct = pickText(show, [
    'host',
    'host_name',
    'hostName',
    'primary_host',
    'primaryHost',
    'primary_host_name',
    'primaryHostName',
    'owner_name',
    'ownerName',
    'creator',
    'creator_name',
    'creatorName',
    'publisher.name',
    'author.name',
  ]);
  if (direct) return direct;
  const listCandidates = [show.hosts, show.host_names, show.hostNames, show.creators, show.people, show.owners];
  for (const list of listCandidates) {
    if (!Array.isArray(list) || list.length === 0) continue;
    const names = list
      .map((entry) =>
        typeof entry === 'string'
          ? entry
          : asText((entry as Record<string, unknown>).name) ||
            asText((entry as Record<string, unknown>).full_name) ||
            asText((entry as Record<string, unknown>).title)
      )
      .filter(Boolean);
    if (names.length) return names.join(', ');
  }
  return '';
};

const toCsvCell = (value: unknown): string => {
  const t = String(value ?? '');
  if (/[",\n]/.test(t)) return `"${t.replace(/"/g, '""')}"`;
  return t;
};

const compactTitle = (value: string, max = 52): string => {
  const t = String(value || '').trim();
  if (!t) return 'Unknown';
  if (t.length <= max) return t;
  return `${t.slice(0, Math.max(1, max - 1)).trimEnd()}…`;
};

const mapPodcastFields = (show: Record<string, any>, fallbackShowId = ''): PodcastTarget => {
  const host = pickHost(show);
  const rawGenres =
    (Array.isArray(show.genres) && show.genres) ||
    (Array.isArray(show.categories) && show.categories) ||
    (Array.isArray(show.category_names) && show.category_names) ||
    [];
  const genres = rawGenres
    .map((g) => (typeof g === 'string' ? g : asText((g as Record<string, unknown>)?.name)))
    .filter(Boolean)
    .join(', ');
  const guestsValue = asBool(show.guests?.accepts_guests ?? show.accepts_guests ?? show.guests);

  return {
    showId: fallbackShowId || normalizeShowId(show),
    title: pickText(show, ['title', 'name', 'podcast.name']) || 'Unknown',
    host: host || undefined,
    publisher:
      pickText(show, [
        'publisher',
        'publisher.name',
        'publisher.title',
        'publisher.display_name',
        'publisherName',
        'author',
        'host',
        'host_name',
        'hostName',
        'owner_name',
        'ownerName',
        'network',
        'organization',
        'organizationName',
        'podcast.publisher',
        'podcast.author',
        'podcast.network',
      ]) || host || undefined,
    email: pickText(show, ['email', 'business_email', 'owner_email', 'contact.email', 'contacts.0.email']),
    contactEmail: pickText(show, ['contact_email', 'booking_email', 'guest_email', 'booking.email', 'contacts.1.email']),
    genres: genres || undefined,
    website: pickText(show, ['website', 'site', 'link', 'web_url', 'podcast.website', 'podcast.web_url']),
    description: pickText(show, ['summary', 'description', 'podcast.description']),
    active: asBool(show.active),
    guests: guestsValue,
    downloadsPerEpisode: pickNumber(show, ['downloads_per_episode', 'downloadsPerEpisode', 'podcast.downloads_per_episode']),
    weeklyDownloads: pickNumber(show, ['est_weekly_downloads', 'estWeeklyDownloads', 'weekly_downloads', 'podcast.est_weekly_downloads']),
    socialReach: pickNumber(show, ['social_reach', 'socialReach']),
    frequency: pickText(show, ['frequency', 'publishing_frequency']),
    totalEpisodes: pickNumber(show, ['num_episodes', 'numEpisodes', 'episodes_count']),
    foundedDate: pickText(show, ['founded', 'created_at', 'podcast.founded']),
    rss: pickText(show, ['rss', 'rssUrl', 'feedUrl', 'feed_url', 'podcast.feed_url', 'podcast.rss']),
    image: imageUrl(show),
  };
};

export function MyListsPodcasts() {
  const { userEmail } = useAppStore();
  const bookingApi = API_CONFIG.BOOKING_API;
  const researchApi = API_CONFIG.RESEARCH_API;
  const [items, setItems] = useState<PodcastTarget[]>([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [detailModal, setDetailModal] = useState<PodcastTarget | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [userLists, setUserLists] = useState<string[]>([]);
  const [activeListName, setActiveListName] = useState(
    () => localStorage.getItem(DEFAULT_LIST_STORAGE_KEY) || 'Podcast_default'
  );
  const [createListModalOpen, setCreateListModalOpen] = useState(false);
  const [newListName, setNewListName] = useState('');

  const persistDefaultList = (listName: string) => {
    setActiveListName(listName);
    localStorage.setItem(DEFAULT_LIST_STORAGE_KEY, listName);
  };

  const loadLists = async () => {
    if (!researchApi || !userEmail) return;
    const res = await fetch(`${researchApi}/get-lists?userEmail=${encodeURIComponent(userEmail)}&entityType=${LIST_ENTITY_TYPE}`);
    const data = await res.json().catch(() => ({}));
    if (!data?.success) return;
    const nextLists = Array.isArray(data.lists) ? data.lists : [];
    const ensured = nextLists.includes(activeListName) ? nextLists : Array.from(new Set([...nextLists, activeListName]));
    setUserLists(ensured.sort((a: string, b: string) => a.localeCompare(b)));
  };

  const load = async () => {
    if (!bookingApi || !userEmail) return;
    setLoading(true);
    try {
      const res = await fetch(`${bookingApi}/targets/list`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ userEmail, listName: activeListName }),
      });
      const data = await res.json().catch(() => ({}));
      const rows = Array.isArray(data?.items) ? data.items : [];
      const next: PodcastTarget[] = rows.map((item: any) => {
        const show = item.show?.raw || item.show || {};
        const showId = item.showId || normalizeShowId(show);
        return { ...mapPodcastFields(show, showId), showId };
      });
      setItems(next);
      setSelected((prev) => new Set(Array.from(prev).filter((id) => next.some((r) => r.showId === id))));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load().catch(() => undefined);
  }, [activeListName, bookingApi, userEmail]);

  useEffect(() => {
    loadLists().catch(() => undefined);
  }, [researchApi, userEmail, activeListName]);

  useEffect(() => {
    if (!bookingApi || !userEmail || items.length === 0) return;
    let cancelled = false;
    const needsHydrate = (row: PodcastTarget) =>
      !row.image || !row.publisher || (!row.email && !row.contactEmail) || !row.genres || !row.description;

    const targets = items.filter(needsHydrate).slice(0, 50);
    if (targets.length === 0) return;

    const hydrate = async () => {
      for (const row of targets) {
        if (cancelled) return;
        try {
          const res = await fetch(`${bookingApi}/podcasts/get`, {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ podcastId: row.showId, userEmail }),
          });
          if (!res.ok) continue;
          const details = (await res.json().catch(() => ({}))) as Record<string, any>;
          const mapped = mapPodcastFields(details, row.showId);
          if (cancelled) return;
          setItems((prev) =>
            prev.map((r) =>
              r.showId !== row.showId
                ? r
                : {
                    ...r,
                    title: r.title || mapped.title,
                    publisher: r.publisher || mapped.publisher,
                    email: r.email || mapped.email,
                    contactEmail: r.contactEmail || mapped.contactEmail,
                    genres: r.genres || mapped.genres,
                    website: r.website || mapped.website,
                    description: r.description || mapped.description,
                    active: r.active ?? mapped.active,
                    guests: r.guests ?? mapped.guests,
                    downloadsPerEpisode: r.downloadsPerEpisode ?? mapped.downloadsPerEpisode,
                    weeklyDownloads: r.weeklyDownloads ?? mapped.weeklyDownloads,
                    socialReach: r.socialReach ?? mapped.socialReach,
                    frequency: r.frequency || mapped.frequency,
                    totalEpisodes: r.totalEpisodes ?? mapped.totalEpisodes,
                    foundedDate: r.foundedDate || mapped.foundedDate,
                    rss: r.rss || mapped.rss,
                    image: r.image || mapped.image,
                  }
            )
          );
        } catch {
          // ignore hydration failures per row
        }
      }
    };
    void hydrate();
    return () => {
      cancelled = true;
    };
  }, [bookingApi, items, userEmail]);

  const removeOne = async (showId: string) => {
    if (!bookingApi || !userEmail) return;
    await fetch(`${bookingApi}/targets/remove`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ userEmail, listName: activeListName, showId }),
    });
    setItems((prev) => prev.filter((row) => row.showId !== showId));
    setSelected((prev) => {
      const next = new Set(prev);
      next.delete(showId);
      return next;
    });
  };

  const removeSelected = async () => {
    if (selected.size === 0) return;
    const ids = Array.from(selected);
    for (const id of ids) {
      await removeOne(id);
    }
    setSelected(new Set());
  };

  const handleCreateList = async () => {
    if (!researchApi || !userEmail) return;
    const trimmed = newListName.trim();
    if (!trimmed) {
      alert('List name required');
      return;
    }
    const res = await fetch(`${researchApi}/upsert-list`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ userEmail, entityType: LIST_ENTITY_TYPE, listName: trimmed }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data?.success) throw new Error(data?.error || 'Failed to create list');
    const canonical = String(data.listName || '').trim();
    setUserLists((prev) => Array.from(new Set([...prev, canonical])).sort((a: string, b: string) => a.localeCompare(b)));
    persistDefaultList(canonical);
    setCreateListModalOpen(false);
    setNewListName('');
  };

  const exportCsv = () => {
    const rows = selected.size > 0 ? filtered.filter((row) => selected.has(row.showId)) : filtered;
    if (!rows.length) return;
    const headers = [
      'Title',
      'Host/Publisher',
      'Email',
      'Contact Email',
      'Genres',
      'Website',
      'Description',
      'Active',
      'Guests',
      'Downloads/Episode',
      'Weekly DLs',
      'Social Reach',
      'Frequency',
      'Total Episodes',
      'Founded Date',
      'Show ID',
    ];
    const body = rows.map((row) =>
      [
        row.title,
        row.publisher || row.host || '',
        row.email || '',
        row.contactEmail || '',
        row.genres || '',
        row.website || '',
        row.description || '',
        row.active === undefined ? '' : row.active ? 'Y' : 'N',
        row.guests === undefined ? '' : row.guests ? 'Y' : 'N',
        row.downloadsPerEpisode ?? '',
        row.weeklyDownloads ?? '',
        row.socialReach ?? '',
        row.frequency || '',
        row.totalEpisodes ?? '',
        row.foundedDate || '',
        row.showId,
      ]
        .map(toCsvCell)
        .join(',')
    );
    const csv = [headers.map(toCsvCell).join(','), ...body].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `podcasts_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const openDetails = async (row: PodcastTarget) => {
    setDetailModal(row);
    if (!bookingApi || !userEmail) return;
    setDetailLoading(true);
    try {
      const res = await fetch(`${bookingApi}/podcasts/get`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ podcastId: row.showId, userEmail }),
      });
      if (!res.ok) return;
      const details = (await res.json().catch(() => ({}))) as Record<string, any>;
      const mapped = mapPodcastFields(details, row.showId);
      const merged: PodcastTarget = {
        ...row,
        ...mapped,
        title: row.title || mapped.title,
        publisher: row.publisher || mapped.publisher,
        email: row.email || mapped.email,
        contactEmail: row.contactEmail || mapped.contactEmail,
        genres: row.genres || mapped.genres,
        website: row.website || mapped.website,
        description: row.description || mapped.description,
        active: row.active ?? mapped.active,
        guests: row.guests ?? mapped.guests,
        downloadsPerEpisode: row.downloadsPerEpisode ?? mapped.downloadsPerEpisode,
        weeklyDownloads: row.weeklyDownloads ?? mapped.weeklyDownloads,
        socialReach: row.socialReach ?? mapped.socialReach,
        frequency: row.frequency || mapped.frequency,
        totalEpisodes: row.totalEpisodes ?? mapped.totalEpisodes,
        foundedDate: row.foundedDate || mapped.foundedDate,
        rss: row.rss || mapped.rss,
        image: row.image || mapped.image,
      };
      setDetailModal(merged);
      setItems((prev) => prev.map((it) => (it.showId === row.showId ? merged : it)));
    } finally {
      setDetailLoading(false);
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((row) =>
      `${row.title} ${row.publisher || ''} ${row.email || ''} ${row.contactEmail || ''} ${row.genres || ''} ${row.website || ''} ${row.description || ''} ${row.rss || ''}`
        .toLowerCase()
        .includes(q)
    );
  }, [items, query]);

  const allSelected = filtered.length > 0 && filtered.every((row) => selected.has(row.showId));

  return (
    <div className="min-h-screen bg-dark-bg text-white">
      <Header />
      <div className="px-6 py-4 border-b border-gray-800">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold">My Lists - Podcasts</h2>
            <p className="text-sm text-gray-400 mt-1">Podcasts saved from Podcast Finder / PodcastIQ target list.</p>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={activeListName}
              onChange={(e) => persistDefaultList(e.target.value)}
              className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white min-w-[220px]"
            >
              {userLists.map((list) => (
                <option key={list} value={list}>
                  {list}
                </option>
              ))}
            </select>
            <button
              onClick={() => setCreateListModalOpen(true)}
              className="bg-gray-800 hover:bg-gray-700 text-white px-4 py-2 rounded-lg text-sm"
            >
              Create List
            </button>
            <button
              onClick={() => load()}
              disabled={loading}
              className="bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white px-4 py-2 rounded-lg text-sm"
            >
              {loading ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
        </div>
      </div>

      {createListModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
          <div className="bg-card-dark border border-gray-700 rounded-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-white mb-2">Create Podcast List</h3>
            <input
              value={newListName}
              onChange={(e) => setNewListName(e.target.value)}
              placeholder="Podcast Outreach"
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded text-sm text-white mb-4"
            />
            <div className="flex items-center justify-end gap-2">
              <button
                className="px-4 py-2 rounded bg-gray-700 hover:bg-gray-600 text-white text-sm"
                onClick={() => {
                  setCreateListModalOpen(false);
                  setNewListName('');
                }}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 rounded bg-red-600 hover:bg-red-700 text-white text-sm"
                onClick={() => {
                  handleCreateList().catch((e) => {
                    console.error(e);
                    alert(e instanceof Error ? e.message : 'Failed to create list');
                  });
                }}
              >
                Create List
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="px-6 py-4 border-b border-gray-800 bg-card-dark">
        <div className="flex flex-wrap items-center gap-3">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search podcasts..."
            className="min-w-[280px] flex-1 max-w-md px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm"
          />
          <label className="inline-flex items-center gap-2 text-sm text-gray-300">
            <input
              type="checkbox"
              checked={allSelected}
              onChange={(e) => {
                if (e.target.checked) setSelected(new Set(filtered.map((row) => row.showId)));
                else setSelected(new Set());
              }}
            />
            Select all
          </label>
          <button
            onClick={() => removeSelected()}
            disabled={selected.size === 0}
            className="px-3 py-2 rounded-lg text-sm bg-red-800 hover:bg-red-700 disabled:opacity-50"
          >
            Remove Selected ({selected.size})
          </button>
          <button
            onClick={exportCsv}
            className="px-3 py-2 rounded-lg text-sm bg-blue-800 hover:bg-blue-700"
          >
            Export CSV {selected.size > 0 ? `(${selected.size})` : ''}
          </button>
          <span className="text-sm text-gray-400">
            Total: <span className="text-white font-semibold">{filtered.length}</span>
          </span>
        </div>
      </div>

      <div className="px-6 py-4">
        <div className="bg-card-dark border border-gray-700 rounded-xl overflow-auto">
          <div className="grid min-w-[2830px] grid-cols-[40px_90px_180px_190px_180px_180px_180px_220px_320px_90px_90px_130px_120px_120px_120px_120px_130px_210px] border-b border-gray-700 text-xs uppercase text-gray-400 font-semibold">
            <div className="px-3 py-3" />
            <div className="px-3 py-3">Art</div>
            <div className="px-3 py-3">Title</div>
            <div className="px-3 py-3">Host/Publisher</div>
            <div className="px-3 py-3">Email</div>
            <div className="px-3 py-3">Contact Email</div>
            <div className="px-3 py-3">Genres</div>
            <div className="px-3 py-3">Website</div>
            <div className="px-3 py-3">Description</div>
            <div className="px-3 py-3">Active</div>
            <div className="px-3 py-3">Guests</div>
            <div className="px-3 py-3">Downloads/Episode</div>
            <div className="px-3 py-3">Weekly DLs</div>
            <div className="px-3 py-3">Social Reach</div>
            <div className="px-3 py-3">Frequency</div>
            <div className="px-3 py-3">Total Episodes</div>
            <div className="px-3 py-3">Founded Date</div>
            <div className="px-3 py-3 text-right">Actions</div>
          </div>
          {filtered.length === 0 ? (
            <div className="px-4 py-10 text-center text-gray-500 text-sm">No podcasts found.</div>
          ) : (
            filtered.map((row) => (
              <div key={row.showId} className="grid min-w-[2830px] grid-cols-[40px_90px_180px_190px_180px_180px_180px_220px_320px_90px_90px_130px_120px_120px_120px_120px_130px_210px] border-b border-gray-800 text-sm items-center">
                <div className="px-3 py-3">
                  <input
                    type="checkbox"
                    checked={selected.has(row.showId)}
                    onChange={(e) => {
                      setSelected((prev) => {
                        const next = new Set(prev);
                        if (e.target.checked) next.add(row.showId);
                        else next.delete(row.showId);
                        return next;
                      });
                    }}
                  />
                </div>
                <div className="px-3 py-3">
                  <div className="w-12 h-12 rounded bg-gray-700 overflow-hidden">
                    {row.image ? <img src={row.image} alt="" className="w-full h-full object-cover" /> : null}
                  </div>
                </div>
                <div className="px-3 py-3 min-w-0">
                  <button
                    className="text-white underline text-left leading-tight"
                    onClick={() => openDetails(row)}
                    title={row.title}
                  >
                    {compactTitle(row.title, 44)}
                  </button>
                  <div className="text-xs text-gray-500 truncate">{row.showId}</div>
                </div>
                <div className="px-3 py-3 text-gray-300 truncate">{row.publisher || row.host || '—'}</div>
                <div className="px-3 py-3 text-gray-300 truncate">{row.email || '—'}</div>
                <div className="px-3 py-3 text-gray-300 truncate">{row.contactEmail || '—'}</div>
                <div className="px-3 py-3 text-gray-300 truncate">{row.genres || '—'}</div>
                <div className="px-3 py-3 text-gray-300 truncate">
                  {row.website ? (
                    <a href={row.website} target="_blank" rel="noreferrer" className="underline">
                      {row.website}
                    </a>
                  ) : (
                    '—'
                  )}
                </div>
                <div className="px-3 py-3 text-gray-300">
                  <button className="truncate text-left underline w-full" onClick={() => openDetails(row)}>
                    {row.description || '—'}
                  </button>
                </div>
                <div className="px-3 py-3 text-gray-300">{row.active === undefined ? '—' : row.active ? 'Y' : 'N'}</div>
                <div className="px-3 py-3 text-gray-300">{row.guests === undefined ? '—' : row.guests ? 'Y' : 'N'}</div>
                <div className="px-3 py-3 text-gray-300">{formatNumber(row.downloadsPerEpisode)}</div>
                <div className="px-3 py-3 text-gray-300">{formatNumber(row.weeklyDownloads)}</div>
                <div className="px-3 py-3 text-gray-300">{formatNumber(row.socialReach)}</div>
                <div className="px-3 py-3 text-gray-300 truncate">{row.frequency || '—'}</div>
                <div className="px-3 py-3 text-gray-300">{formatNumber(row.totalEpisodes)}</div>
                <div className="px-3 py-3 text-gray-300">{formatDate(row.foundedDate)}</div>
                <div className="px-3 py-3 flex justify-end gap-2">
                  <button
                    onClick={() => openDetails(row)}
                    className="px-2 py-1 rounded bg-blue-700 hover:bg-blue-600 text-xs"
                  >
                    View
                  </button>
                  {row.website ? (
                    <a
                      href={row.website}
                      target="_blank"
                      rel="noreferrer"
                      className="px-2 py-1 rounded bg-gray-700 hover:bg-gray-600 text-xs"
                    >
                      Open
                    </a>
                  ) : null}
                  <button
                    onClick={() => removeOne(row.showId)}
                    className="px-2 py-1 rounded bg-red-800 hover:bg-red-700 text-xs"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {detailModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-6">
          <div className="bg-gradient-to-b from-gray-900 to-gray-950 border border-gray-700 rounded-2xl max-w-6xl w-full max-h-[88vh] overflow-auto shadow-2xl">
            <div className="sticky top-0 z-10 border-b border-gray-800 bg-gray-900/95 backdrop-blur px-6 py-4 flex items-center justify-between">
              <div className="min-w-0">
                <div className="text-[11px] uppercase tracking-[0.18em] text-gray-500">Podcast Profile</div>
                <div className="text-xl font-bold text-white truncate" title={detailModal.title}>{detailModal.title}</div>
                <div className="text-sm text-gray-300 truncate">{detailModal.host || detailModal.publisher || '—'}</div>
              </div>
              <button
                className="px-3 py-1.5 rounded-lg bg-gray-800 hover:bg-gray-700 text-sm text-gray-200"
                onClick={() => setDetailModal(null)}
              >
                Close
              </button>
            </div>
            <div className="p-6 space-y-6">
              {detailLoading ? <div className="text-sm text-amber-300">Refreshing podcast details…</div> : null}

              <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6">
                <div className="space-y-3">
                  <div className="w-48 h-48 rounded-xl bg-gray-800 border border-gray-700 overflow-hidden">
                    {detailModal.image ? <img src={detailModal.image} alt="" className="w-full h-full object-cover" /> : null}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <span className={`px-2.5 py-1 rounded-full text-xs border ${detailModal.active ? 'bg-emerald-500/20 border-emerald-500/40 text-emerald-200' : 'bg-gray-700/60 border-gray-600 text-gray-300'}`}>
                      Active: {detailModal.active === undefined ? '—' : detailModal.active ? 'Y' : 'N'}
                    </span>
                    <span className={`px-2.5 py-1 rounded-full text-xs border ${detailModal.guests ? 'bg-indigo-500/20 border-indigo-500/40 text-indigo-200' : 'bg-gray-700/60 border-gray-600 text-gray-300'}`}>
                      Guests: {detailModal.guests === undefined ? '—' : detailModal.guests ? 'Y' : 'N'}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="rounded-xl border border-gray-800 bg-gray-900/60 p-4">
                    <div className="text-xs uppercase text-gray-500">Host/Publisher</div>
                    <div className="text-sm text-gray-100 mt-1">{detailModal.publisher || detailModal.host || '—'}</div>
                  </div>
                  <div className="rounded-xl border border-gray-800 bg-gray-900/60 p-4">
                    <div className="text-xs uppercase text-gray-500">Email</div>
                    <div className="text-sm text-gray-100 mt-1 break-all">{detailModal.email || '—'}</div>
                  </div>
                  <div className="rounded-xl border border-gray-800 bg-gray-900/60 p-4">
                    <div className="text-xs uppercase text-gray-500">Contact Email</div>
                    <div className="text-sm text-gray-100 mt-1 break-all">{detailModal.contactEmail || '—'}</div>
                  </div>
                  <div className="rounded-xl border border-gray-800 bg-gray-900/60 p-4">
                    <div className="text-xs uppercase text-gray-500">Website</div>
                    <div className="text-sm text-gray-100 mt-1 break-all">
                      {detailModal.website ? (
                        <a className="underline text-blue-300" href={detailModal.website} target="_blank" rel="noreferrer">
                          {detailModal.website}
                        </a>
                      ) : (
                        '—'
                      )}
                    </div>
                  </div>
                  <div className="rounded-xl border border-gray-800 bg-gray-900/60 p-4">
                    <div className="text-xs uppercase text-gray-500">Genres</div>
                    <div className="text-sm text-gray-100 mt-1">{detailModal.genres || '—'}</div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                <div className="rounded-xl border border-gray-800 bg-gray-900/70 p-3">
                  <div className="text-[11px] uppercase text-gray-500">Downloads/Episode</div>
                  <div className="text-lg font-semibold text-white">{formatNumber(detailModal.downloadsPerEpisode)}</div>
                </div>
                <div className="rounded-xl border border-gray-800 bg-gray-900/70 p-3">
                  <div className="text-[11px] uppercase text-gray-500">Weekly DLs</div>
                  <div className="text-lg font-semibold text-white">{formatNumber(detailModal.weeklyDownloads)}</div>
                </div>
                <div className="rounded-xl border border-gray-800 bg-gray-900/70 p-3">
                  <div className="text-[11px] uppercase text-gray-500">Social Reach</div>
                  <div className="text-lg font-semibold text-white">{formatNumber(detailModal.socialReach)}</div>
                </div>
                <div className="rounded-xl border border-gray-800 bg-gray-900/70 p-3">
                  <div className="text-[11px] uppercase text-gray-500">Frequency</div>
                  <div className="text-lg font-semibold text-white">{detailModal.frequency || '—'}</div>
                </div>
                <div className="rounded-xl border border-gray-800 bg-gray-900/70 p-3">
                  <div className="text-[11px] uppercase text-gray-500">Total Episodes</div>
                  <div className="text-lg font-semibold text-white">{formatNumber(detailModal.totalEpisodes)}</div>
                </div>
                <div className="rounded-xl border border-gray-800 bg-gray-900/70 p-3">
                  <div className="text-[11px] uppercase text-gray-500">Founded Date</div>
                  <div className="text-lg font-semibold text-white">{formatDate(detailModal.foundedDate)}</div>
                </div>
              </div>

              <div className="rounded-xl border border-gray-800 bg-gray-900/60 p-4">
                <div className="text-xs uppercase tracking-[0.16em] text-gray-500 mb-2">Description</div>
                <div className="text-sm text-gray-200 whitespace-pre-wrap leading-relaxed">{detailModal.description || '—'}</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
